import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { isConfigured } from '../services/firebaseConfig';
import { AlertTriangle, Fingerprint, Scan, XCircle, CheckCircle } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { KaiAvatar } from './KaiAvatar';

export const LoginPage: React.FC = () => {
    const { signInWithGoogle, loginAsGuest } = useAuth();
    const [isHolding, setIsHolding] = useState(false);
    const [progress, setProgress] = useState(0);
    const [loginTriggered, setLoginTriggered] = useState(false);
    const [shakeIntensity, setShakeIntensity] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const holdTimerRef = useRef<number>(0);
    const startTimeRef = useRef<number>(0);

    useEffect(() => {
        if (localStorage.getItem('ikigai_account_deleted')) {
            setSuccessMessage("Identity and data successfully purged.");
            localStorage.removeItem('ikigai_account_deleted');
            // Auto-hide after 5s
            setTimeout(() => setSuccessMessage(null), 5000);
        }
    }, []);

    // --- HOLD INTERACTION LOGIC ---
    const startHold = () => {
        if (loginTriggered) return;
        setIsHolding(true);
        setErrorMessage(null); // Clear previous errors
        startTimeRef.current = Date.now();

        const animate = () => {
            const elapsed = Date.now() - startTimeRef.current;
            // Slower hold time (2s) for more dramatic tension
            const newProgress = Math.min(100, (elapsed / 2000) * 100);

            setProgress(newProgress);

            // Haptic Visuals: Shake screen as we get closer
            if (newProgress > 70) {
                setShakeIntensity((newProgress - 70) / 5); // 0 to 6px shake
            } else {
                setShakeIntensity(0);
            }

            if (newProgress < 100) {
                holdTimerRef.current = requestAnimationFrame(animate);
            } else {
                setLoginTriggered(true);
                setShakeIntensity(0);
                handleLogin(); // Trigger!
            }
        };

        holdTimerRef.current = requestAnimationFrame(animate);
    };

    const endHold = () => {
        if (loginTriggered) return;
        setIsHolding(false);
        setProgress(0);
        setShakeIntensity(0);
        cancelAnimationFrame(holdTimerRef.current);
    };

    const handleLogin = async () => {
        try {
            // Add a small artificial delay for the "Success" animation to play
            await new Promise(r => setTimeout(r, 800));
            await signInWithGoogle();
        } catch (e: any) {
            console.error("Login failed", e);
            setLoginTriggered(false);
            setProgress(0);
            setIsHolding(false);

            // Friendly Error Handling
            const msg = e.code || e.message || '';
            if (msg.includes('unauthorized-domain') || msg.includes('operation-not-allowed')) {
                setErrorMessage("Domain not authorized in Firebase Console. Please add this domain or use Guest Mode.");
            } else if (msg.includes('popup-closed-by-user')) {
                setErrorMessage("Sign-in cancelled.");
            } else {
                setErrorMessage("Authentication failed. Check configuration.");
            }
        }
    };

    const handleGuest = async () => {
        setLoginTriggered(true);
        setErrorMessage(null);
        await loginAsGuest();
    };

    return (
        <div className="relative min-h-screen w-full overflow-hidden bg-[#030305] text-white flex flex-col items-center justify-center font-sans selection:bg-indigo-500/30 perspective-1000">

            {/* --- GLOBAL ANIMATION STYLES --- */}
            <style dangerouslySetInnerHTML={{
                __html: `
            @keyframes nebula-spin {
                0% { transform: rotate(0deg) scale(1); }
                50% { transform: rotate(180deg) scale(1.1); }
                100% { transform: rotate(360deg) scale(1); }
            }
            @keyframes star-float {
                0% { transform: translateY(0px); opacity: 0.3; }
                50% { transform: translateY(-20px); opacity: 0.8; }
                100% { transform: translateY(0px); opacity: 0.3; }
            }
            @keyframes scan-line {
                0% { top: 0%; opacity: 0; }
                10% { opacity: 1; }
                90% { opacity: 1; }
                100% { top: 100%; opacity: 0; }
            }
            @keyframes scale-up {
                0% { transform: scale(0.8); opacity: 0; }
                100% { transform: scale(1); opacity: 1; }
            }
            @keyframes sonar-ping {
                0% { transform: scale(1); opacity: 0.8; }
                100% { transform: scale(2); opacity: 0; }
            }
            @keyframes breathing-glow {
                0% { opacity: 0.6; transform: scale(0.95); filter: drop-shadow(0 0 5px rgba(99,102,241,0.4)); }
                50% { opacity: 1; transform: scale(1.05); filter: drop-shadow(0 0 15px rgba(99,102,241,0.7)); }
                100% { opacity: 0.6; transform: scale(0.95); filter: drop-shadow(0 0 5px rgba(99,102,241,0.4)); }
            }
            .nebula-layer {
                mix-blend-mode: screen;
                animation: nebula-spin 60s linear infinite;
            }
            .shake-ui {
                animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both infinite;
            }
        `}} />

            {/* 1. THE DEEP SPACE ENGINE (Background) */}
            <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
                {/* Base Gradient */}
                <div className="absolute inset-0 bg-radial-gradient from-[#1a1b2e] to-[#000000]"></div>

                {/* Nebula 1 (Indigo Core) */}
                <div className="absolute top-[-20%] left-[-20%] w-[140%] h-[140%] bg-gradient-to-br from-indigo-900/20 via-transparent to-transparent rounded-full blur-[100px] nebula-layer duration-[120s]"></div>

                {/* Nebula 2 (Violet Plasma) */}
                <div className="absolute bottom-[-20%] right-[-20%] w-[140%] h-[140%] bg-gradient-to-tl from-violet-900/20 via-transparent to-transparent rounded-full blur-[120px] nebula-layer duration-[90s] direction-reverse"></div>

                {/* Stars / Dust */}
                <div className="absolute inset-0 opacity-40" style={{
                    backgroundImage: 'radial-gradient(white 1px, transparent 0)',
                    backgroundSize: '60px 60px',
                    animation: 'star-float 10s ease-in-out infinite'
                }}></div>

                {/* Scanline Overlay */}
                <div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,20,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] z-[1] background-size-[100%_2px,3px_100%] pointer-events-none"></div>
            </div>

            {/* 2. TOP CONTROLS (Floating) */}
            <div className="absolute top-8 right-8 z-50 flex items-center gap-4 animate-fade-in-down">
                {!isConfigured && (
                    <div className="flex items-center gap-2 px-4 py-1.5 bg-amber-500/10 border border-amber-500/20 text-amber-500 text-[10px] font-bold uppercase tracking-widest rounded-full backdrop-blur-md">
                        <AlertTriangle size={12} /> Demo Mode
                    </div>
                )}
                <ThemeToggle />
            </div>

            {/* 3. THE INTERFACE (Shakable Container) */}
            <div
                className="relative z-20 flex flex-col items-center gap-12 max-w-lg w-full"
                style={{ transform: `translate(${Math.random() * shakeIntensity}px, ${Math.random() * shakeIntensity}px)` }}
            >

                {/* Avatar & Branding */}
                <div className="flex flex-col items-center text-center space-y-6">
                    <div className="relative group cursor-pointer transition-transform duration-700 hover:scale-105">
                        {/* Holographic Projector Light */}
                        <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-1 bg-indigo-500 blur-lg transition-all duration-300 ${isHolding ? 'w-64 opacity-100' : 'opacity-50'}`}></div>

                        <KaiAvatar inline size={160} whisper="Sync your biometrics to begin." />

                        {/* Orbital Rings */}
                        <div className={`absolute inset-[-20px] border border-indigo-500/20 rounded-full transition-all duration-1000 pointer-events-none ${isHolding ? 'scale-110 border-indigo-400/40 rotate-180' : 'scale-100 rotate-0'}`}></div>
                        <div className={`absolute inset-[-40px] border border-dashed border-violet-500/10 rounded-full transition-all duration-[20s] animate-spin-slow pointer-events-none ${isHolding ? 'opacity-100' : 'opacity-50'}`}></div>
                    </div>

                    <div className="space-y-2">
                        <h1 className="text-6xl md:text-8xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-white/20 drop-shadow-2xl">
                            KAI
                        </h1>
                        <p className="text-lg md:text-xl text-indigo-200/60 font-light tracking-[0.2em] uppercase">
                            Life Operating System
                        </p>
                    </div>
                </div>

                {/* Error Message Display */}
                {errorMessage && (
                    <div className="animate-fade-in-up bg-red-900/30 border border-red-500/50 text-red-200 px-6 py-3 rounded-xl flex items-center gap-3 text-sm max-w-sm text-left">
                        <XCircle size={20} className="shrink-0 text-red-500" />
                        <div>{errorMessage}</div>
                    </div>
                )}

                {/* Success Message Display (Post-Deletion) */}
                {successMessage && (
                    <div className="animate-fade-in-up bg-green-900/30 border border-green-500/50 text-green-200 px-6 py-3 rounded-xl flex items-center gap-3 text-sm max-w-sm text-left">
                        <CheckCircle size={20} className="shrink-0 text-green-500" />
                        <div>{successMessage}</div>
                    </div>
                )}

                {/* 4. THE BIOMETRIC LOCK (Button) */}
                <div className="relative mt-4">

                    {/* Sonar Ping (Attention Grabber) - Only when NOT holding */}
                    {!isHolding && !loginTriggered && (
                        <div className="absolute inset-0 w-full h-full rounded-full border border-indigo-500/30 bg-indigo-500/5 z-0"
                            style={{ animation: 'sonar-ping 3s infinite ease-out' }}>
                        </div>
                    )}

                    {/* Resonance Field (Expands on hold) */}
                    <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-500/20 blur-xl transition-all duration-100 ease-out pointer-events-none ${isHolding ? 'w-64 h-64 opacity-100' : 'w-24 h-24 opacity-0'
                        }`}></div>

                    {/* Progress Ring SVG */}
                    <svg className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 -rotate-90 pointer-events-none" viewBox="0 0 100 100">
                        {/* Track */}
                        <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="2" />
                        {/* Fill */}
                        <circle
                            cx="50" cy="50" r="46" fill="none" stroke={loginTriggered ? "#4ade80" : "#818cf8"} strokeWidth="3"
                            strokeDasharray="289" // 2 * pi * 46
                            strokeDashoffset={289 - (289 * progress) / 100}
                            strokeLinecap="round"
                            className={`transition-all ease-linear ${loginTriggered ? 'duration-500' : 'duration-75'}`}
                            style={{ filter: 'drop-shadow(0 0 4px rgba(129, 140, 248, 0.8))' }}
                        />
                    </svg>

                    {/* The Physical Button */}
                    <button
                        onMouseDown={startHold}
                        onMouseUp={endHold}
                        onMouseLeave={endHold}
                        onTouchStart={startHold}
                        onTouchEnd={endHold}
                        disabled={loginTriggered}
                        className={`relative w-28 h-28 rounded-full flex flex-col items-center justify-center transition-all duration-300 group overflow-hidden border backdrop-blur-md z-10 ${loginTriggered
                            ? 'bg-green-500/20 border-green-500/50 scale-110 shadow-[0_0_50px_rgba(74,222,128,0.4)]'
                            : isHolding
                                ? 'scale-95 bg-indigo-600/30 border-indigo-400 shadow-[inset_0_0_20px_rgba(99,102,241,0.5)]'
                                : 'bg-white/5 hover:bg-white/10 border-white/10 hover:border-white/30 hover:scale-105'
                            }`}
                    >
                        {loginTriggered ? (
                            <div className="animate-scale-up">
                                <Scan size={40} className="text-green-400 mb-1" />
                            </div>
                        ) : (
                            <>
                                <div
                                    className={`relative z-10 transition-all duration-300 ${isHolding ? 'scale-110 opacity-100' : 'opacity-80'}`}
                                    style={!isHolding ? { animation: 'breathing-glow 3s infinite ease-in-out' } : undefined}
                                >
                                    <Fingerprint size={48} className={isHolding ? "text-indigo-300 animate-pulse" : "text-white"} />
                                </div>

                                {/* Scanning Line Effect */}
                                {isHolding && (
                                    <div className="absolute top-0 left-0 w-full h-[2px] bg-indigo-400 blur-[2px] animate-scan-line shadow-[0_0_10px_#818cf8]"></div>
                                )}

                                <span className={`absolute bottom-3 text-[9px] font-black uppercase tracking-widest transition-all duration-300 ${isHolding ? 'translate-y-8 opacity-0' : 'text-indigo-200/60 group-hover:text-white'
                                    }`}>
                                    Hold
                                </span>
                            </>
                        )}
                    </button>
                </div>

                {/* Status Text */}
                <div className="h-6 flex items-center justify-center">
                    {loginTriggered ? (
                        <span className="text-green-400 text-xs font-bold uppercase tracking-widest animate-pulse flex items-center gap-2">
                            <Scan size={14} /> Identity Verified
                        </span>
                    ) : isHolding ? (
                        <span className="text-indigo-300 text-xs font-bold uppercase tracking-widest animate-pulse">
                            Synchronizing Neural Link... {Math.round(progress)}%
                        </span>
                    ) : (
                        isConfigured && (
                            <button
                                onClick={handleGuest}
                                className="text-[10px] text-white/30 hover:text-white transition-colors uppercase tracking-[0.2em] font-medium border-b border-transparent hover:border-white/50 pb-0.5"
                            >
                                Or Initialize Guest Protocol
                            </button>
                        )
                    )}
                </div>

            </div>
        </div>
    );
};